import React, { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Bank, FileText, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Shared form for logging an off-platform bank deposit and receiving a PDF
 * proof-of-payment. Used in 3 places:
 *  - LoadFundsButton (as a tab next to "Pay with card")
 *  - VirtualCard receiver-card detail screen (inline button)
 *  - Top-level /direct-deposit page
 *
 * Phase 1 = manual log only. Customer wires from their own bank externally;
 * this records the details and generates a receipt.
 */
export function DirectDepositForm({ cardId = null, onSuccess = null, compact = false }) {
  const [stage, setStage] = useState("form"); // form | success
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({
    receiver_name: "",
    receiver_email: "",
    receiver_phone: "",
    bank_name: "",
    bank_address: "",
    bank_country: "",
    account_number: "",
    routing_number: "",
    swift_code: "",
    iban: "",
    intermediary_bank_name: "",
    intermediary_swift: "",
    amount: "",
    currency: "USD",
    memo: "",
  });
  const [wireType, setWireType] = useState("domestic_us"); // 'domestic_us' | 'international'
  const [showIntermediary, setShowIntermediary] = useState(false);
  const [result, setResult] = useState(null);
  const [bicLookup, setBicLookup] = useState(null); // { valid, bank_name, bank_city, country_name, match_type, error }
  const [bicSuggestions, setBicSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const lookupTimer = useRef(null);
  const searchTimer = useRef(null);
  const swiftWrapRef = useRef(null);

  // ISO 3166-1 alpha-2 → unicode regional-indicator flag emoji
  const flagFor = (cc) => {
    if (!cc || cc.length !== 2) return "";
    const A = 0x1F1E6;
    const code = cc.toUpperCase();
    return String.fromCodePoint(A + code.charCodeAt(0) - 65) +
           String.fromCodePoint(A + code.charCodeAt(1) - 65);
  };

  // Click outside the SWIFT block → close suggestions
  useEffect(() => {
    const onClick = (e) => {
      if (swiftWrapRef.current && !swiftWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced live autocomplete — runs as the user types in SWIFT field.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = (data.swift_code || "").trim();
    if (q.length < 2) {
      setBicSuggestions([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/public/bic-search?q=${encodeURIComponent(q)}&limit=8`);
        setBicSuggestions(r.data?.matches || []);
      } catch (e) {
        setBicSuggestions([]);
      }
    }, 200);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [data.swift_code]);

  // Debounced live SWIFT/BIC lookup. Runs ~500ms after the user stops typing.
  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const code = (data.swift_code || "").trim().toUpperCase();
    if (!code) {
      setBicLookup(null);
      return undefined;
    }
    if (code.length < 8) {
      // Too short to validate — show neutral hint
      setBicLookup({ valid: false, error: `Type ${8 - code.length} more characters…`, soft: true });
      return undefined;
    }
    lookupTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/public/bic-lookup/${encodeURIComponent(code)}`);
        setBicLookup(r.data);
        // Auto-fill bank name and city if registry hit AND fields are empty
        if (r.data?.match_type === "registry") {
          setData((d) => ({
            ...d,
            bank_name: d.bank_name?.trim() ? d.bank_name : r.data.bank_name || "",
            bank_address: d.bank_address?.trim() ? d.bank_address
              : (r.data.bank_city && r.data.country_name
                  ? `${r.data.bank_city}, ${r.data.country_name}`
                  : d.bank_address),
          }));
        }
      } catch (e) {
        setBicLookup({ valid: false, error: "Could not validate. Please double-check.", soft: true });
      }
    }, 500);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  }, [data.swift_code]);

  // Pick a suggestion: fills SWIFT + bank fields and closes the dropdown.
  const pickSuggestion = (m) => {
    setData((d) => ({
      ...d,
      swift_code: m.code,
      bank_name: m.bank_name || d.bank_name,
      bank_address: m.bank_city && m.country_name
        ? `${m.bank_city}, ${m.country_name}`
        : d.bank_address,
    }));
    setShowSuggestions(false);
  };

  // ABA routing-number checksum. The 9-digit US ABA routing number satisfies
  // (3·(d1+d4+d7) + 7·(d2+d5+d8) + 1·(d3+d6+d9)) mod 10 === 0
  // This catches single-digit typos before the wire is submitted. Returns
  // null when input isn't 9 digits (so we don't yell at half-typed values).
  const routingChecksum = (() => {
    const r = (data.routing_number || "").replace(/\D/g, "");
    if (r.length === 0) return null;
    if (r.length !== 9) return { valid: false, soft: true, msg: `${9 - r.length} digit${9 - r.length === 1 ? "" : "s"} to go…` };
    const d = r.split("").map(Number);
    const sum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + 1 * (d[2] + d[5] + d[8]);
    if (sum % 10 !== 0) {
      return { valid: false, soft: false, msg: "Routing number checksum failed — please re-check the digits." };
    }
    return { valid: true, msg: "Valid ABA routing number." };
  })();

  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.receiver_name.trim() || !data.bank_name.trim() ||
        !data.amount || Number(data.amount) <= 0) {
      toast.error("Please fill receiver name, bank, and amount");
      return;
    }
    // Wire-type-aware validation
    if (wireType === "domestic_us") {
      if (!data.account_number.trim() || !data.routing_number.trim()) {
        toast.error("Account + routing number required for US wires");
        return;
      }
    } else {
      const hasIban = !!data.iban.trim();
      const hasAcctSwift = !!data.account_number.trim() && !!data.swift_code.trim();
      if (!hasIban && !hasAcctSwift) {
        toast.error("For international: provide either IBAN, OR account number + SWIFT/BIC");
        return;
      }
      if (!data.bank_address.trim()) {
        toast.error("Bank address (branch + city + country) is required for international wires");
        return;
      }
    }
    setBusy(true);
    try {
      const r = await api.post("/direct-deposits", {
        ...data,
        amount: Number(data.amount),
        currency: data.currency || "USD",
        card_id: cardId,
      });
      setResult(r.data);
      setStage("success");
      if (r.data.auto_approved) {
        toast.success(`Deposit logged · Receipt ${r.data.reference_number}`);
      } else {
        toast.message(`Queued for admin review (above $${r.data.threshold_usd.toLocaleString()} threshold)`);
      }
      if (onSuccess) onSuccess(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log deposit");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!result?.id) return;
    try {
      const r = await api.get(`/direct-deposits/${result.id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcardon-deposit-${result.reference_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download receipt");
    }
  };

  const emailReceipt = async () => {
    if (!result?.id) return;
    try {
      await api.post(`/direct-deposits/${result.id}/email-receipt`);
      toast.success("Receipt emailed to receiver");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not email receipt");
    }
  };

  const notifyReceiver = async () => {
    if (!result?.id) return;
    try {
      const r = await api.post(`/direct-deposits/${result.id}/notify-receiver`);
      const ch = [];
      if (r.data?.email_sent) ch.push("email");
      if (r.data?.sms_sent) ch.push("SMS");
      if (ch.length === 0) {
        toast.message("Notification sent (no channel confirmed)");
      } else {
        toast.success(`Notified receiver via ${ch.join(" + ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not notify receiver");
    }
  };

  const startCheckout = async () => {
    if (!result?.id) return;
    try {
      const r = await api.post(`/direct-deposits/${result.id}/checkout`);
      if (r.data?.checkout_url) {
        // Hard navigate so Stripe Checkout takes over the tab. After payment
        // Stripe redirects back to /direct-deposits?paid=<id>&session_id=…
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start payment");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    }
  };

  if (stage === "success") {
    const queued = !result.auto_approved;
    return (
      <div className="space-y-3" data-testid="direct-deposit-success">
        <div className={`rounded-xl border p-4 ${
          queued
            ? "border-[#CBA35C]/40 bg-[#CBA35C]/10 text-[#8C6B21]"
            : "border-[#184A30]/30 bg-[#184A30]/5 text-[#184A30]"
        }`}>
          <p className="serif text-xl">
            {queued ? "Pending admin review" : "Deposit logged"}
          </p>
          <p className="text-sm mt-1">
            Reference: <span className="mono font-semibold">{result.reference_number}</span>
          </p>
          {queued && (
            <p className="text-xs mt-2">
              This wire is over the ${result.threshold_usd.toLocaleString()} threshold and needs admin
              approval before you can pay. You'll be notified when approved.
            </p>
          )}
          {result.receipt_email_sent && (
            <p className="text-xs mt-2">✓ Receipt emailed to receiver</p>
          )}
        </div>

        {!queued && (
          <Button
            onClick={startCheckout}
            className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-12 text-base"
            data-testid="direct-deposit-pay-checkout"
          >
            💳 Pay ${Number(data.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} with debit / credit card
          </Button>
        )}

        {!queued && (
          <p className="text-xs text-[#4A5C50] -mt-1 leading-relaxed">
            You'll be redirected to Stripe to enter your card. Once paid, Zcardon will wire the funds to {data.receiver_name || "the receiver"}'s bank within 1 business day. You'll get an email confirmation.
          </p>
        )}

        {!queued && (
          <>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadPdf} className="flex-1 rounded-xl border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5" data-testid="direct-deposit-download-pdf">
                <DownloadSimple size={16} className="mr-2" /> Download PDF receipt
              </Button>
              <Button variant="outline" onClick={emailReceipt} className="rounded-xl" data-testid="direct-deposit-email-receipt">
                Email receipt
              </Button>
            </div>
            {(result.receiver_email || result.receiver_phone || data.receiver_email || data.receiver_phone) && (
              <Button
                onClick={notifyReceiver}
                className="w-full rounded-xl bg-[#CBA35C] hover:bg-[#B08A44] text-[#112318] font-semibold"
                data-testid="direct-deposit-notify-receiver"
              >
                📣 Notify receiver: funds are on the way
              </Button>
            )}
          </>
        )}
        <Button variant="outline" onClick={() => { setStage("form"); setResult(null); setData({ receiver_name:"", receiver_email:"", receiver_phone:"", bank_name:"", bank_address:"", account_number:"", routing_number:"", swift_code:"", amount:"", memo:"" }); }} className="w-full rounded-xl">
          Log another deposit
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4"} data-testid="direct-deposit-form">
      <div className="rounded-xl border border-[#E5E0D8] bg-[#F8F4ED] p-3">
        <p className="text-[11px] text-[#4A5C50] leading-relaxed">
          <Bank size={14} weight="duotone" className="inline mr-1" />
          <span className="font-semibold">How this works:</span> After logging the deposit, you'll pay via
          Stripe Checkout (card or ACH). Funds land in your Zcardon balance, then we wire them to the
          receiver's bank — automatically via Stripe Payouts where supported, or manually for other
          countries. Auto-approved under $1,000; larger amounts queue for admin review.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver full name *</Label>
          <Input value={data.receiver_name} onChange={set("receiver_name")} placeholder="Jane Doe" className="mt-1" data-testid="dd-receiver-name" required />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Amount *</Label>
          <div className="flex gap-1.5 mt-1">
            <Input type="number" min="0.01" step="0.01" value={data.amount} onChange={set("amount")} placeholder="50.00" className="mono flex-1" data-testid="dd-amount" required />
            <select
              value={data.currency}
              onChange={set("currency")}
              className="rounded-md border border-[#E5E0D8] bg-white text-sm mono px-2"
              data-testid="dd-currency"
            >
              {["USD", "EUR", "GBP", "VND", "JPY", "THB", "AUD", "CAD", "SGD", "INR", "HKD", "PHP", "IDR", "MYR", "AED", "MXN", "BRL", "CHF", "CNY"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver email</Label>
          <Input type="email" value={data.receiver_email} onChange={set("receiver_email")} placeholder="jane@example.com" className="mt-1" data-testid="dd-receiver-email" />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver phone</Label>
          <Input type="tel" value={data.receiver_phone} onChange={set("receiver_phone")} placeholder="+84 912 345 678" className="mt-1 mono" data-testid="dd-receiver-phone" />
        </div>
      </div>

      {/* Wire-type toggle */}
      <div className="border border-[#E5E0D8] rounded-lg p-3 bg-[#F8F4ED]/40" data-testid="dd-wire-type-toggle">
        <Label className="text-xs tracking-widest uppercase text-[#4A5C50] block mb-2">Destination bank</Label>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setWireType("domestic_us")}
            className={`flex-1 text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border transition ${
              wireType === "domestic_us"
                ? "bg-[#184A30] text-white border-[#184A30]"
                : "border-[#E5E0D8] text-[#4A5C50] hover:border-[#184A30]"
            }`}
            data-testid="dd-wire-us"
          >
            🇺🇸 US bank
          </button>
          <button
            type="button"
            onClick={() => setWireType("international")}
            className={`flex-1 text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border transition ${
              wireType === "international"
                ? "bg-[#184A30] text-white border-[#184A30]"
                : "border-[#E5E0D8] text-[#4A5C50] hover:border-[#184A30]"
            }`}
            data-testid="dd-wire-intl"
          >
            🌍 International
          </button>
        </div>
        <p className="mt-2 text-[11px] text-[#4A5C50] leading-snug">
          {wireType === "domestic_us"
            ? "Use this for banks in the United States. You'll need the account + 9-digit ABA routing number."
            : "Use this for banks outside the US (Vietnam, EU, UK, Asia, etc.). You'll need SWIFT/BIC + IBAN or account number + bank address."}
        </p>
      </div>

      <div>
        <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Bank name *</Label>
        <Input value={data.bank_name} onChange={set("bank_name")} placeholder={wireType === "domestic_us" ? "Bank of America" : "Vietcombank"} className="mt-1" data-testid="dd-bank-name" required />
      </div>
      <div>
        <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
          Bank address {wireType === "international" ? "*" : ""} <span className="opacity-50">(branch / city / country)</span>
        </Label>
        <Input
          value={data.bank_address}
          onChange={set("bank_address")}
          placeholder={wireType === "domestic_us" ? "100 N Tryon St, Charlotte, NC 28255, USA" : "198 Tran Quang Khai, Hanoi, Vietnam"}
          className="mt-1"
          data-testid="dd-bank-address"
          required={wireType === "international"}
        />
      </div>

      {wireType === "domestic_us" ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Account number *</Label>
            <Input value={data.account_number} onChange={set("account_number")} className="mt-1 mono" data-testid="dd-account" required />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Routing number *</Label>
            <Input value={data.routing_number} onChange={set("routing_number")} className="mt-1 mono" data-testid="dd-routing" required />
            {routingChecksum && (
              <div
                className={`mt-1.5 flex items-center gap-1.5 text-[11px] ${
                  routingChecksum.valid
                    ? "text-[#184A30]"
                    : routingChecksum.soft
                      ? "text-[#4A5C50]"
                      : "text-[#B04E2F]"
                }`}
                data-testid="dd-routing-checksum"
              >
                {routingChecksum.valid ? (
                  <CheckCircle size={13} weight="fill" />
                ) : (
                  <WarningCircle size={13} weight={routingChecksum.soft ? "regular" : "fill"} />
                )}
                <span>{routingChecksum.msg}</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
              IBAN <span className="opacity-50">(for EU / UK — skip otherwise)</span>
            </Label>
            <Input
              value={data.iban}
              onChange={(e) => setData((d) => ({ ...d, iban: e.target.value.toUpperCase().replace(/\s+/g, "") }))}
              placeholder="DE89 3704 0044 0532 0130 00"
              className="mt-1 mono uppercase"
              maxLength={34}
              data-testid="dd-iban"
            />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
              Account number <span className="opacity-50">(if no IBAN)</span>
            </Label>
            <Input
              value={data.account_number}
              onChange={set("account_number")}
              placeholder="0123456789"
              className="mt-1 mono"
              data-testid="dd-account-intl"
            />
          </div>
        </div>
      )}
      <div ref={swiftWrapRef} className="relative">
        <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
          SWIFT / BIC code {wireType === "international" ? "*" : ""}{" "}
          <span className="opacity-50">
            {wireType === "international" ? "(required — start typing bank name or code)" : "(international wires only)"}
          </span>
        </Label>
        <Input
          value={data.swift_code}
          onChange={(e) => {
            setData((d) => ({ ...d, swift_code: e.target.value.toUpperCase().slice(0, 11) }));
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="BOFAUS3N · or type 'Barclays', 'HSBC', …"
          maxLength={11}
          className="mt-1 mono uppercase"
          data-testid="dd-swift"
          autoComplete="off"
        />
        {showSuggestions && bicSuggestions.length > 0 && (
          <ul
            className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E5E0D8] rounded-xl shadow-lg max-h-72 overflow-y-auto"
            data-testid="dd-swift-suggestions"
          >
            {bicSuggestions.map((m) => (
              <li
                key={m.code}
                role="option"
                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(m); }}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8F4ED] border-b last:border-b-0 border-[#F0EBE2]"
                data-testid={`dd-swift-option-${m.code}`}
              >
                <span className="text-xl leading-none flex-shrink-0">{flagFor(m.country_code)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#112318] truncate">{m.bank_name}</p>
                  <p className="text-[11px] text-[#4A5C50] truncate">{m.bank_city}{m.country_name && ` · ${m.country_name}`}</p>
                </div>
                <span className="text-[11px] mono text-[#184A30] flex-shrink-0">{m.code}</span>
              </li>
            ))}
          </ul>
        )}
        {bicLookup && (
          <div
            className={`mt-2 rounded-lg border px-3 py-2 text-[12px] flex items-start gap-2 ${
              bicLookup.match_type === "registry"
                ? "border-[#184A30]/30 bg-[#184A30]/5 text-[#184A30]"
                : bicLookup.valid
                  ? "border-[#CBA35C]/40 bg-[#CBA35C]/10 text-[#8C6B21]"
                  : bicLookup.soft
                    ? "border-[#E5E0D8] bg-[#F8F4ED] text-[#4A5C50]"
                    : "border-[#D96C4A]/40 bg-[#D96C4A]/5 text-[#B04E2F]"
            }`}
            data-testid="dd-swift-lookup"
          >
            {bicLookup.match_type === "registry" ? (
              <CheckCircle size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
            ) : (
              <WarningCircle size={16} weight={bicLookup.valid ? "regular" : "fill"} className="mt-0.5 flex-shrink-0" />
            )}
            <div className="leading-snug">
              {bicLookup.match_type === "registry" && (
                <>
                  <b>{bicLookup.bank_name}</b>
                  <span className="block opacity-80 mt-0.5">{bicLookup.bank_city} · {bicLookup.country_name} — bank name and address auto-filled.</span>
                </>
              )}
              {bicLookup.match_type === "format_only" && (
                <>
                  <b>Valid BIC format</b> for {bicLookup.country_name || `country ${bicLookup.country_code}`}.
                  <span className="block opacity-80 mt-0.5">Bank not in our registry — please confirm the bank name manually.</span>
                </>
              )}
              {!bicLookup.valid && (
                <span>{bicLookup.error}</span>
              )}
            </div>
          </div>
        )}
      </div>
      {wireType === "international" && (
        <details
          className="border border-[#E5E0D8] rounded-lg p-3"
          open={showIntermediary}
          onToggle={(e) => setShowIntermediary(e.currentTarget.open)}
          data-testid="dd-intermediary-section"
        >
          <summary className="text-xs tracking-widest uppercase text-[#4A5C50] cursor-pointer hover:text-[#184A30]">
            Intermediary / correspondent bank <span className="opacity-50">(optional — only if receiver's bank requires it)</span>
          </summary>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Intermediary bank name</Label>
              <Input value={data.intermediary_bank_name} onChange={set("intermediary_bank_name")} placeholder="Citibank N.A." className="mt-1" data-testid="dd-intermediary-name" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Intermediary SWIFT</Label>
              <Input
                value={data.intermediary_swift}
                onChange={(e) => setData((d) => ({ ...d, intermediary_swift: e.target.value.toUpperCase() }))}
                placeholder="CITIUS33"
                maxLength={11}
                className="mt-1 mono uppercase"
                data-testid="dd-intermediary-swift"
              />
            </div>
          </div>
        </details>
      )}

      <div>
        <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Memo</Label>
        <Textarea value={data.memo} onChange={set("memo")} placeholder="Reason for transfer…" className="mt-1" rows={2} data-testid="dd-memo" />
      </div>
      <Button type="submit" disabled={busy} className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-11" data-testid="dd-submit">
        <FileText size={16} weight="duotone" className="mr-2" />
        {busy ? "Logging deposit…" : "Log deposit and generate receipt"}
      </Button>
    </form>
  );
}

export default DirectDepositForm;
